import { Card } from "primereact/card";
import { CardAction, CardStats } from "./card.types";
import { useIcon } from "../../hooks";
import { FabrixIcon, createUUID } from "../../common";
import { Toolbar } from "primereact/toolbar";
import { CSSProperties, DragEventHandler, useCallback, useEffect, useState } from "react";
import { Chip } from "primereact/chip";

export interface BasicFrabixCardProps {
    title: string;
    id?: string;
    item?: any;
    tags?: string[];
    description?: string;
    startActions?: CardAction[];
    endActions?: CardAction[];
    stats?: CardStats[];
    icon?: FabrixIcon | string
    onClick?: (e: any, item?: any) => void;
    disabled?: boolean;
    sideColor?: string;
    className?: string;
    onDrop?: (e: any, item?: any) => void;
    onDrag?: (e: any, item?: any) => void;
    onDragOver?: (e: any, item?: any) => void;
    draggable?: boolean;
}


/**
 * Displays a customizable Fabrix card with optional actions, tags, stats, and drag support.
 * @param props Configuration including title, content metadata, event handlers, and visuals.
 * @returns PrimeReact Card element tailored with Fabrix styling.
 */
export function BasicFrabixCard(props: BasicFrabixCardProps) {

    const icon = useIcon();
    const [_disabled, setDisabled] = useState<boolean>(props.disabled ?? false)

    useEffect(() => { setDisabled(props.disabled ?? false) }, [props.disabled])

    /**
     * Executes the associated card action while preventing parent click propagation.
     * @param e Mouse click event from the action element.
     * @param action Action metadata describing the behavior.
     */
    const handleActionClick = (e: any, action: CardAction) => {
        e.stopPropagation();
        if (action.onClick && props.disabled !== true && action.disabled !== true)
            action.onClick(e, props.item)
    }

    /**
     * Renders the action toolbar section on the left side of the card footer.
     */
    const startActions = useCallback(() => {

        return (
            <div className="fabrix-card-actions-start">
                {
                    props.startActions?.map(x => {
                        let caption = x.iconOnly ? '' : x.caption;
                        let color =  x.disabled ? 'silver' :  (x.color ? x.color : 'black');
                        let className = `${x.className ?? ""} ${x.icon ? icon(x.icon) : ''} fabrix-card-action`

                        return (
                            <span key={createUUID()} style={{ color: color }} title={x.caption} className={className} onClick={(e) => handleActionClick(e, x)}>{caption}</span>
                        )
                    })
                }
            </div>)
    }, [_disabled, props.startActions])

    /**
     * Renders the action toolbar section on the right side of the card footer.
     */
    const endActions = useCallback(() => {

        return (
            <div className="fabrix-card-actions-end">
                {
                    props.endActions?.map(x => {
                        let caption = x.iconOnly ? '' : x.caption;
                        let color =  x.disabled ? 'silver' :  (x.color ? x.color : 'black');
                        let className = `${x.className} ${x.icon ? icon(x.icon) : ''} fabrix-card-action`

                        return (
                            <span key={createUUID()} title={x.caption} style={{ color: color }} className={className} onClick={(e) => handleActionClick(e, x)}>{caption}</span>
                        )
                    })
                }
            </div>)
    }, [_disabled, props.endActions])

    /**
     * Builds the footer toolbar combining start and end actions.
     */
    const footer = useCallback(() => {
        return (
            <Toolbar start={startActions()} end={endActions()} className="fabrix-card-footer-toolbar" />
        )
    }, [_disabled, props.startActions, props.endActions])

    const renderTitle = (
        <div style={{ display: 'flex' }}>
            <span className={`${props.icon ? icon(props.icon) : ''} fabrix-card-title-icon`}></span>
            <span style={{ whiteSpace: 'nowrap' }} className="fabrix-card-title">{props.title}</span>
        </div>
    )

    const renderStats = (
        <div className="inline-flex">
            {
                props.stats?.map(x => {
                    let className = `${x.icon ? icon(x.icon) : ''} fabrix-card-stats-icon`
                    return (<span title={x.tooltip} key={createUUID()} style={{ color: x.color }} className="fabrix-card-stats-item flex">
                        <span className={className}></span>
                        <span className="fabrix-card-stats-display">{x.display}</span>
                    </span>)
                })
            }
        </div>
    )

    /**
     * Invokes the primary card click handler when the card is enabled.
     * @param e Mouse event emitted from the card body.
     */
    const handleClick = (e: any) => {
        if (props.onClick && props.disabled !== true) {
            props.onClick(e, props.item)
        }
    }

    const style: CSSProperties = props.sideColor ? { borderLeftWidth: 4, borderLeftStyle: "solid", borderLeftColor: `#${props.sideColor}` } : {}

    /**
     * Forwards drop events to consumers for custom drag-and-drop handling.
     * @param e Drop event triggered on the card.
     */
    const handleDrop = (e: any) => {
        if (props.onDrop) {
            props.onDrop(e, props.item)
        }
    }

    /**
     * Exposes drag-over events to consumers when the card is not disabled.
     * @param e Drag event occurring over the card.
     */
    const handleDragOver = (e: any) => {
        if (props.onDragOver && props.disabled !== true) {
            props.onDragOver(e, props.item)
        }
    }

    /**
     * Exposes drag events to consumers when the card is not disabled.
     * @param e Drag event fired by the card.
     */
    const handleDrag = (e: any) => {
        if (props.onDrag && props.disabled !== true) {
            props.onDrag(e, props.item);
        }
    }
    return (
        <Card onDrop={handleDrop} draggable={props.draggable} onDrag={handleDrag} onDragOver={handleDragOver} title={renderTitle} id={props.id} subTitle={props.description} style={style}
            footer={footer()} className={`${props.className ?? ''} fabrix-card-basic ${props.onClick ? "card-clickable" : ''}`} onClick={handleClick}>
            
            <div className="fabrix-card-tags">
                {
                    props.tags?.map((tag: string, index: number) => {
                        return (
                            <Chip key={`card_${index}_${tag}`} className="fabrix-card-tag" label={tag} />
                        )
                    })
                }
            </div>
            <div className="fabrix-card-stats">
                {
                    renderStats
                }
            </div>
        </Card>
    )
}